/* ============ WORK INDEX — sector filter + hover/focus preview ============
 * Powers /work/ only — no-ops on every other page (guarded on #work-preview
 * / [data-project-list] presence, per the shared-bundle contract in
 * main.js). Reads project data straight off the server-rendered DOM
 * (data-* attributes on each <li>, the <img> already in its thumbnail) —
 * assets/js/data.js is a build-time-only module and is never bundled here.
 *
 * Hover/focus parity: mouseenter and focusin share the exact same handler,
 * routed through one delegated listener on the list. mouseenter doesn't
 * bubble, so it's registered with useCapture so a single ancestor listener
 * still catches it for every descendant; focusin already bubbles. Both
 * paths call the same updatePreview(), so hovering a project and tabbing to
 * it produce an identical result — there is no separate touch/keyboard code
 * path to drift out of sync.
 *
 * Plain <a> elements keep native tab order and native Enter/Space
 * navigation; this module only reacts to the focus/hover side effect, it
 * never intercepts a click or keypress.
 */

function initWorkIndex() {
  const list = document.querySelector('[data-project-list]');
  const preview = document.getElementById('work-preview');
  if (!list || !preview) return; // Not the /work/ index page.

  const items = Array.from(list.querySelectorAll('li[data-slug]'));
  let activeItem = list.querySelector('.is-active') || items[0] || null;

  const previewImg = preview.querySelector('[data-preview-img]');
  const previewName = preview.querySelector('[data-preview-name]');
  const previewLoc = preview.querySelector('[data-preview-loc]');
  const previewYears = preview.querySelector('[data-preview-years]');
  const previewValue = preview.querySelector('[data-preview-value]');
  const previewScope = preview.querySelector('[data-preview-scope]');

  function updatePreview(item) {
    if (!item || item === activeItem) return;
    if (activeItem) activeItem.classList.remove('is-active');
    item.classList.add('is-active');
    activeItem = item;

    const thumbImg = item.querySelector('img');
    if (previewImg) {
      if (thumbImg) previewImg.src = thumbImg.src;
      previewImg.alt = `${item.dataset.name}, a facade installation in ${item.dataset.loc}.`;
      previewImg.decoding = 'async';
    }
    if (previewName) previewName.textContent = item.dataset.name || '';
    if (previewLoc) previewLoc.textContent = item.dataset.loc || '';
    if (previewYears) previewYears.textContent = item.dataset.years || '';
    if (previewValue) previewValue.textContent = item.dataset.value || '';
    if (previewScope) previewScope.textContent = item.dataset.scope || '';
  }

  // Single shared handler for both hover and keyboard focus — this is what
  // guarantees the two stay in lockstep.
  function handleHoverOrFocus(event) {
    const item = event.target.closest('li[data-slug]');
    if (!item || !list.contains(item)) return;
    updatePreview(item);
  }

  // mouseenter does not bubble; useCapture lets one listener on the list
  // still see it for every descendant.
  list.addEventListener('mouseenter', handleHoverOrFocus, true);
  list.addEventListener('focusin', handleHoverOrFocus);

  initSectorFilter(list, items);
}

function initSectorFilter(list, items) {
  const filterButtons = Array.from(document.querySelectorAll('[data-sector-filter]'));
  const countEl = document.querySelector('[data-sector-count]');
  if (!filterButtons.length) return;

  const validSectors = filterButtons.map((btn) => btn.dataset.sectorFilter);

  function applyFilter(sector, { updateHistory = true } = {}) {
    let visible = 0;
    items.forEach((item) => {
      const match = sector === 'all' || item.dataset.sector === sector;
      item.hidden = !match;
      if (match) visible += 1;
    });

    filterButtons.forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.sectorFilter === sector));
    });

    if (countEl) {
      countEl.textContent = `Showing ${visible} of ${items.length} projects`;
    }

    if (updateHistory) {
      const url = new URL(window.location.href);
      if (sector === 'all') {
        url.searchParams.delete('sector');
      } else {
        url.searchParams.set('sector', sector);
      }
      window.history.replaceState(null, '', url);
    }
  }

  filterButtons.forEach((btn) => {
    btn.addEventListener('click', () => applyFilter(btn.dataset.sectorFilter));
  });

  const requestedSector = new URLSearchParams(window.location.search).get('sector');
  const initialSector = requestedSector && validSectors.includes(requestedSector) ? requestedSector : 'all';
  applyFilter(initialSector, { updateHistory: false });
}

export { initWorkIndex };
